import { skipToken } from '@reduxjs/toolkit/query/react'
import { TeamListPage, TeamMemberRow } from '@numeo/types-main-web-app'
import { useMemo } from 'react'
import { useAppSelector } from '@/hooks'
import { useListTeamMembersQuery } from '@/store/api/teamApi'
import { listMembersFromUsers, normalizeMembersQuery, TEAM_MAX_IDS, TeamMembersQuery } from '@/utils/team/teamQuery'
import { listStateOf, READY, TeamListState } from './teamListState'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

interface TeamListOptions {
    /** Ask nothing (a closed modal, a missing id). */
    skip?: boolean
}

/**
 * Whether a query asks for a whole list of members (the first 200, no search, no ids): every such read is answered from ONE
 * shared read of all members, its roles filtered here, so a page holding dispatcher, email-sender and assignee lists asks
 * once. Pure.
 */
const isWholeMembersList = (query: TeamMembersQuery): boolean => !query.search && !query.ids && (query.page ?? 1) === 1 && query.limit === TEAM_MAX_IDS

const SHARED_MEMBERS: TeamMembersQuery = { page: 1, limit: TEAM_MAX_IDS }

/** The shared read narrowed to these roles, as a page of its own. */
const withRoles = (page: TeamListPage<TeamMemberRow>, roles: TeamMembersQuery['roles']): TeamListPage<TeamMemberRow> => {
    if (!roles?.length) return page
    const items = page.items.filter((row) => roles.includes(row.role))
    return { items, pagination: { ...page.pagination, total: items.length } }
}

/**
 * The company's members (admins, dispatchers, accountants) by name, a page at a time (100 by default), with the server's
 * search across all of them: `GET /team/members/v1`, cached five minutes per query. A whole list (`isWholeMembersList`) is
 * filtered from the one shared read of every member, unless the company has more than 200, when it is asked for itself. A
 * company the directory does not serve yet gets the same page computed from `application.users`.
 */
export const useTeamMembers = (query: TeamMembersQuery = {}, options: TeamListOptions = {}): TeamListState<TeamMemberRow> => {
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = JSON.stringify(normalizeMembersQuery(query))
    const normalized = useMemo((): TeamMembersQuery => JSON.parse(key), [key])
    const skip = !!options.skip || (normalized.ids !== undefined && normalized.ids.length === 0)
    const asking = enabled && !!applicationId && !skip

    const whole = isWholeMembersList(normalized)
    const shared = useListTeamMembersQuery(asking && whole && applicationId ? { applicationId, ...SHARED_MEMBERS } : skipToken)
    // The shared read covers the company only while it holds every member; past 200, a role list is asked for itself.
    const sharedCovers = !!shared.data && shared.data.pagination.total <= shared.data.items.length
    const ownRead = asking && (!whole || (!!shared.data && !sharedCovers))
    const remote = useListTeamMembersQuery(ownRead && applicationId ? { applicationId, ...normalized } : skipToken)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))
    const ownerId = useAppSelector((state) => (state.auth.application?.owner ? String(state.auth.application.owner) : undefined))
    const local = useMemo(() => (enabled || skip ? undefined : listMembersFromUsers(users, ownerId, normalized)), [enabled, skip, users, ownerId, normalized])
    const fromShared = useMemo(
        () => (shared.data && sharedCovers ? withRoles(shared.data, normalized.roles) : undefined),
        [shared.data, sharedCovers, normalized.roles]
    )

    if (!enabled) return listStateOf(local, READY)
    if (skip) return listStateOf(undefined, READY)
    if (whole && !ownRead) return listStateOf(fromShared, { isLoading: shared.isLoading, isFetching: shared.isFetching, isError: shared.isError })
    return listStateOf(remote.data, { isLoading: remote.isLoading, isFetching: remote.isFetching, isError: remote.isError })
}
